
import { CanadidateReportInfo, TypedVue } from "@/store/types";
import { Component, Prop } from "vue-property-decorator";
import { ProjectTheme } from "@/store/admin/adminTypes";
import { Trait, FOUR_GROUP_TRAIT_GROUPINGS, TRAIT_CLASS_RANGES } from "@/lib/consts";
import ComparisonRadarChartComponent, { ComparisonAssessmentItem, ComparisonBenchmarkItem } from "@/components/admin/ComparisonComponents/ComparisonRadarChartComponent.vue";
import { getOrientationText, getTraitContent, getTraitDevSuggestions, OrientationTextResult } from "@/lib/elraReport7DynamicText";

const ORIENTATION_META: Record<string, { label: string; description: string }> = {
  "strategic-orientation": {
    label: "Strategic Orientation",
    description: "How much a leader focuses on big-picture, future-oriented thinking, new ways of doing things, and planning how to achieve the company's vision and strategy.",
  },
  "poeple-orientation": {
    label: "People Orientation",
    description: "How much a leader focuses on supporting others' growth and well-being and championing social impact.",
  },
  "outcome-orientation": {
    label: "Outcome Orientation",
    description: "How much a leader prioritizes pursuing targets, enforcing process, and persisting through obstacles.",
  },
  "communication-orientation": {
    label: "Communication Orientation",
    description: "How a leader communicates to share information, build relationships, and influence others.",
  },
};

interface TraitInfo {
  key: Trait;
  displayName: string;
  priority: "high" | "mixed" | "low";
  score: number;
  benchmarkMean: number;
  definition: string;
  behaviours: string[];
  devSuggestions: string[];
}

interface OrientationGroup {
  id: string;
  label: string;
  description: string;
  traits: TraitInfo[];
  highlyPrioritizedTraits: TraitInfo[];
  prioritizedTraits: TraitInfo[];
  lessPrioritizedTraits: TraitInfo[];
  prioritizedCount: number;
  totalCount: number;
  emphasisLabel: string;
  orientationText: OrientationTextResult;
}

@Component({ components: { ComparisonRadarChartComponent } })
export default class ElraReport7 extends TypedVue {
  @Prop({ required: true }) public data!: CanadidateReportInfo;
  @Prop({ required: true }) private showPII!: boolean;
  @Prop() traitClassRanges!: Record<Trait, { low: number; high: number; lowerlow: number; mean: number; standardDeviation: number }>;
  @Prop({ default: ProjectTheme.DEFAULT_TA }) private theme!: string;
  @Prop({ default: () => [] }) radarCandidates!: ComparisonAssessmentItem[];
  @Prop({ default: () => [] }) radarBenchmarks!: ComparisonBenchmarkItem[];
  @Prop({ default: false }) radarShowNames!: boolean;
  @Prop({ default: false }) radarAllowModeSwitch!: boolean;
  @Prop({ default: "competency" }) radarMode!: string;
  @Prop({ default: "" }) radarColorUniqueKey!: string;
  @Prop({ default: false }) radarFourGroup!: boolean;
  @Prop({ default: true }) showAiInsights!: boolean;

  activeOrientationTab = "strategic-orientation";
  expandedTraits: Record<string, boolean> = {};

  readonly infoTiles: { icon: string; title: string; text: string; popoverText?: string }[] = [
    {
      icon: "bi bi-grid",
      title: "Competency Framework",
      text: "You were assessed against 15 Leadership Competencies grouped into 4 categories, derived from the System-3™ Leadership Competency Framework.",
    },
    {
      icon: "bi bi-cpu",
      title: "Prioritization Logic",
      text: "We measured how much you lean on a competency relative to others when making decisions and the trade-offs you make under pressure. The more you prioritize a competency, the more consistently you choose behaviors aligned with it, suggesting it plays a meaningful role in how you lead.",
      popoverText:
        "<ul class='pl-3 mb-0'>" +
        "<li><strong>Highly Prioritized:</strong> Competencies you prioritize to a greater extent than the S-3 Benchmark™, suggesting a strong, dominant expression of these Competencies in your leadership approach.</li>" +
        "<li><strong>Prioritized:</strong> Competencies you prioritize to a similar extent as the S-3 Benchmark™. These Leadership Competencies are not the dominant defining features of your leadership approach, but you do use them in your decision-making, potentially more selectively.</li>" +
        "<li><strong>Less Prioritized:</strong> Competencies you prioritize to a lesser extent than the S-3 Benchmark™, signaling that you emphasize them less in your leadership approach. You may be overlooking these core Leadership Competencies when navigating complex business decisions.</li>" +
        "</ul>",
    },
    {
      icon: "bi bi-arrows-angle-expand",
      title: "Benchmark Comparison",
      text: "Your results show how your decision patterns align with or differ from the S-3 Benchmark™.",
      popoverText: "The S-3 Benchmark™ is based on the average performance of current, high-performing, and diverse senior leaders",
    },
    {
      icon: "bi bi-compass",
      title: "What You'll See",
      text: "Your report includes your Orientation Summaries, Competency Results, and Developmental Next Steps.",
    },
  ];

  get candidateDisplayName(): string {
    return this.showPII ? this.data.data.candidate_name : this.data.data.assessmentId.substring(0, 4).toUpperCase();
  }

  get readiness(): string {
    return this.data.readiness || "default";
  }

  get orientationGroups(): OrientationGroup[] {
    return Object.entries(FOUR_GROUP_TRAIT_GROUPINGS).map(([orientationId, traits]) => {
      const meta = ORIENTATION_META[orientationId];

      const scoring = this.data.data.scoring ?? {};

      const traitInfos: TraitInfo[] = (traits as Trait[]).map((trait) => {
        const traitScore: number = this.data.data.scoring?.[`${trait}_bellcurve`] ?? 0;
        const cutoffs = this.traitClassRanges?.[trait] ?? TRAIT_CLASS_RANGES[trait];
        const priority: "high" | "mixed" | "low" = traitScore >= cutoffs.high ? "high" : traitScore >= cutoffs.low ? "mixed" : "low";
        const benchmarkMean: number = cutoffs.mean ?? 50;
        const displayName = this.$t(`Comparison::TraitChart::XAxisLabel::${trait}`).toString();
        const { definition, behaviours } = getTraitContent(trait);
        const devSuggestions = getTraitDevSuggestions(trait);
        return { key: trait, displayName, priority, score: traitScore, benchmarkMean, definition, behaviours, devSuggestions };
      });

      const highlyPrioritizedTraits = traitInfos.filter((t) => t.priority === "high");
      const prioritizedTraits = traitInfos.filter((t) => t.priority === "mixed");
      const lessPrioritizedTraits = traitInfos.filter((t) => t.priority === "low");
      const prioritizedCount = traitInfos.filter((t) => t.priority !== "low").length;
      const totalCount = traitInfos.length;

      const highPct = highlyPrioritizedTraits.length / totalCount;
      const lowPct = lessPrioritizedTraits.length / totalCount;
      let emphasisLabel = "Variable Emphasis";
      if (highPct >= 0.6) emphasisLabel = "Strong Emphasis";
      else if (lowPct >= 0.6) emphasisLabel = "Limited Emphasis";

      return {
        id: orientationId,
        label: meta?.label ?? orientationId,
        description: meta?.description ?? "",
        traits: traitInfos,
        highlyPrioritizedTraits,
        prioritizedTraits,
        lessPrioritizedTraits,
        prioritizedCount,
        totalCount,
        emphasisLabel,
        orientationText: getOrientationText(orientationId, scoring),
      };
    });
  }

  get lessPrioritizedTraits(): TraitInfo[] {
    return this.orientationGroups
      .flatMap((og) => og.lessPrioritizedTraits)
      .sort((a, b) => a.score - b.score)
      .slice(0, 3);
  }

  formatSentence(text: string): string {
    if (!text) return text;
    const result = text.charAt(0).toUpperCase() + text.slice(1);
    return result.endsWith(".") ? result : result + ".";
  }

  toggleTrait(traitKey: string): void {
    this.$set(this.expandedTraits, traitKey, !this.expandedTraits[traitKey]);
  }

  mounted() {
    this.$nextTick(() => {
      window.dispatchEvent(
        new CustomEvent("report-template-updated", {
          detail: { container: this.$el },
        }),
      );
    });

    this.$matomo && this.$matomo.trackEvent("Report", "Loaded", this.data.version, null, { shortId: this.data.id, projectId: this.data.projectId, version: this.data.version });
  }
}
